import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  Quote,
  Sparkles,
  ShieldCheck,
  Clock,
  Zap,
  ArrowLeft,
  MessageCircle,
} from "lucide-react";
import { KENTRO } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import {
  CLINICAS_VERTICALS,
  CLINICAS_FEATURES,
  CLINICAS_FAQ,
  type ClinicaVertical,
} from "@/lib/clinicas-data";
import { trackIaNav, trackEvent, trackMetaPixel, trackMetaPixelCustom, withUtm } from "@/lib/analytics";

const SITE = "https://kentro.com.br";
const IA_URL = "https://formulario.kentro.com.br/medico";

const VALID_SLUGS = ["medicas", "odontologicas", "esteticas"] as const;
type VerticalSlug = (typeof VALID_SLUGS)[number];

function isValidSlug(s: string): s is VerticalSlug {
  return (VALID_SLUGS as readonly string[]).includes(s);
}

function getVertical(slug: string): ClinicaVertical | undefined {
  return CLINICAS_VERTICALS.find((v) => v.slug === slug);
}

const SEO: Record<VerticalSlug, { title: string; description: string; h1Suffix: string }> = {
  medicas: {
    title:
      "Kentro para Clínicas Médicas — Reduza no-show e atenda no WhatsApp Oficial com IA",
    description:
      "Plataforma omnichannel com IA Dr., confirmação automática de consultas, roteamento por convênio e CRM para clínicas médicas. Menos faltas, mais agendamentos.",
    h1Suffix: "clínica médica",
  },
  odontologicas: {
    title:
      "Kentro para Clínicas Odontológicas — Menos faltas e mais orçamentos fechados",
    description:
      "IA responde valores, planos e parcelamento 24h. Lembretes com pré-pagamento, follow-up de tratamentos e CRM para clínicas odontológicas.",
    h1Suffix: "clínica odontológica",
  },
  esteticas: {
    title:
      "Kentro para Clínicas de Estética — Transforme leads do Instagram em pacotes fechados",
    description:
      "IA qualifica interesse por procedimento, agenda avaliação e faz recall de sessões. CRM para clínicas de estética focado em conversão e retenção.",
    h1Suffix: "clínica de estética",
  },
};

export const Route = createFileRoute("/clinicas/$vertical")({
  loader: ({ params }) => {
    if (!isValidSlug(params.vertical)) throw notFound();
    return { verticalSlug: params.vertical };
  },
  head: ({ params }) => {
    if (!isValidSlug(params.vertical)) {
      return {
        meta: [
          { title: "Vertical não encontrada — Kentro" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const seo = SEO[params.vertical];
    const url = `${SITE}/clinicas/${params.vertical}`;
    const vertical = getVertical(params.vertical)!;
    return {
      meta: [
        { title: seo.title },
        { name: "description", content: seo.description },
        {
          name: "keywords",
          content: `${vertical.name.toLowerCase()}, whatsapp para clínica, ia para clínica, crm para clínica, no-show, agendamento, kentro`,
        },
        { property: "og:type", content: "website" },
        { property: "og:title", content: seo.title },
        { property: "og:description", content: seo.description },
        { property: "og:url", content: url },
        { property: "og:image", content: `${SITE}/og/og-default.png` },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: seo.title },
        { name: "twitter:description", content: seo.description },
        { name: "twitter:image", content: `${SITE}/og/og-default.png` },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "@id": `${url}#service`,
            name: `Kentro para ${vertical.name}`,
            serviceType: `Plataforma omnichannel com IA para ${vertical.name.toLowerCase()}`,
            provider: { "@type": "Organization", name: "Kentro Sistemas", url: SITE },
            areaServed: "BR",
            audience: {
              "@type": "Audience",
              audienceType: vertical.name,
            },
            description: seo.description,
            url,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
              { "@type": "ListItem", position: 2, name: "Clínicas", item: `${SITE}/clinicas` },
              { "@type": "ListItem", position: 3, name: vertical.name, item: url },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: CLINICAS_FAQ.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="text-3xl font-bold">Vertical de clínica não encontrada</h1>
      <p className="mt-3 text-muted-foreground">
        Escolha uma das opções disponíveis: médicas, odontológicas ou estéticas.
      </p>
      <Button asChild className="mt-6">
        <Link to="/clinicas">Voltar para Clínicas</Link>
      </Button>
    </div>
  ),
  errorComponent: ({ reset }) => (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="text-2xl font-bold">Não foi possível carregar esta página</h1>
      <Button className="mt-6" onClick={() => reset()}>Tentar novamente</Button>
    </div>
  ),
  component: VerticalPage,
});

function VerticalPage() {
  const { verticalSlug } = Route.useLoaderData();
  const vertical = getVertical(verticalSlug)!;
  return (
    <>
      <Hero vertical={vertical} />
      <Metrics vertical={vertical} />
      <PainAndWins vertical={vertical} />
      <SharedFeatures vertical={vertical} />
      <TestimonialBlock vertical={vertical} />
      <Faq />
      <FinalCta vertical={vertical} />
    </>
  );
}

/* ---------------- HERO ---------------- */
function Hero({ vertical }: { vertical: ClinicaVertical }) {
  const Icon = vertical.icon;
  return (
    <section className="relative overflow-hidden border-b border-white/5 py-20 md:py-28">
      <div
        className="absolute inset-0 -z-10 opacity-40"
        style={{ background: "var(--gradient-hero)" }}
      />
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground">
          <Link to="/" className="hover:text-foreground">Home</Link>
          <span>/</span>
          <Link to="/clinicas" className="hover:text-foreground">Clínicas</Link>
          <span>/</span>
          <span className="text-foreground">{vertical.shortName}</span>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <span className="gradient-primary inline-flex size-14 items-center justify-center rounded-2xl text-white shadow-glow">
            <Icon className="size-7" />
          </span>
          <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {vertical.name}
          </span>
        </div>

        <h1
          className="mt-6 max-w-4xl text-4xl font-bold leading-tight md:text-5xl lg:text-6xl"
          style={{ fontFamily: "var(--font-display)" }}
        >
          Kentro para <span className="gradient-text">{vertical.name}</span>
        </h1>
        <p className="mt-5 max-w-2xl text-lg text-muted-foreground">{vertical.tagline}</p>

        <div className="mt-8 flex flex-wrap gap-3">
          <Button asChild size="lg" className="gradient-primary text-white shadow-glow">
            <Link
              to="/contato"
              search={{ src: "clinicas", vertical: vertical.slug }}
              onClick={() => {
                trackEvent("demo_cta_click", {
                  location: "clinicas_vertical_hero",
                  page: `clinicas_${vertical.slug}`,
                  vertical: vertical.slug,
                  target: "form_contato",
                });
                trackMetaPixelCustom("ScheduleDemo", {
                  source: `clinicas_${vertical.slug}`,
                  location: "clinicas_vertical_hero",
                  page: `clinicas_${vertical.slug}`,
                  vertical: vertical.slug,
                });
              }}
            >
              Agendar demonstração <ArrowRight className="ml-1 size-4" />
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline">
            <a
              href={withUtm(IA_URL, {
                utm_source: "site_kentro",
                utm_medium: `landing_clinicas_${vertical.slug}`,
                utm_campaign: "experimente_ia_dr",
                utm_content: "hero",
              })}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                trackIaNav("clinicas_hero", "clinicas", { ia_slug: "medico" });
                trackEvent("ia_try_click", {
                  location: "clinicas_hero",
                  ia_slug: "medico",
                });
              }}
            >
              <Sparkles className="mr-1 size-4" /> Testar IA Dr. no WhatsApp
            </a>
          </Button>
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck className="size-4 text-primary-glow" /> WhatsApp Oficial (Meta)
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clock className="size-4 text-primary-glow" /> IA 24/7
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Zap className="size-4 text-primary-glow" /> Onboarding em 7–15 dias
          </span>
        </div>
      </div>
    </section>
  );
}

/* ---------------- METRICS ---------------- */
function Metrics({ vertical }: { vertical: ClinicaVertical }) {
  return (
    <section className="border-b border-white/5 bg-white/[0.02] py-10">
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 px-4 sm:px-6 md:grid-cols-3 lg:px-8">
        {vertical.metrics.map((m) => (
          <div key={m.label} className="text-center md:text-left">
            <div
              className="gradient-text text-2xl font-bold md:text-3xl"
              style={{ fontFamily: "var(--font-display)" }}
            >
              {m.value}
            </div>
            <div className="mt-1 text-sm text-muted-foreground">{m.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------------- PAINS / WINS ---------------- */
function PainAndWins({ vertical }: { vertical: ClinicaVertical }) {
  return (
    <section className="py-16 md:py-24">
      <div className="mx-auto grid max-w-6xl gap-8 px-4 sm:px-6 md:grid-cols-2 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="glass-card rounded-2xl p-6 md:p-8"
        >
          <div className="text-[11px] font-semibold uppercase tracking-wider text-red-400/90">
            O que trava a sua {vertical.shortName.toLowerCase()}
          </div>
          <ul className="mt-4 space-y-3 text-muted-foreground">
            {vertical.pains.map((p) => (
              <li key={p} className="flex gap-2">
                <span className="mt-2 size-1.5 shrink-0 rounded-full bg-red-400/70" />
                {p}
              </li>
            ))}
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.05 }}
          className="glass-card rounded-2xl p-6 md:p-8"
        >
          <div className="text-[11px] font-semibold uppercase tracking-wider text-primary-glow">
            O que a Kentro entrega
          </div>
          <ul className="mt-4 space-y-3">
            {vertical.wins.map((w) => (
              <li key={w} className="flex items-start gap-2 text-sm">
                <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary-glow" />
                {w}
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>
  );
}

/* ---------------- SHARED FEATURES ---------------- */
function SharedFeatures({ vertical }: { vertical: ClinicaVertical }) {
  return (
    <section className="border-y border-white/5 bg-white/[0.02] py-16 md:py-20">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <div className="text-sm font-semibold uppercase tracking-wide text-primary-glow">
            Núcleo da plataforma
          </div>
          <h2
            className="mt-2 text-3xl font-bold md:text-4xl"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Recursos que sua <span className="gradient-text">{vertical.shortName.toLowerCase()}</span> ganha no dia 1.
          </h2>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {CLINICAS_FEATURES.map((f) => (
            <div key={f.title} className="glass-card rounded-2xl p-6">
              <h3 className="font-bold" style={{ fontFamily: "var(--font-display)" }}>
                {f.title}
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- TESTIMONIAL ---------------- */
function TestimonialBlock({ vertical }: { vertical: ClinicaVertical }) {
  return (
    <section className="py-16 md:py-20">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <figure className="glass-card rounded-2xl p-8 md:p-10">
          <Quote className="size-6 text-primary-glow/80" />
          <blockquote className="mt-4 text-lg leading-relaxed text-foreground/90 md:text-xl">
            "{vertical.testimonial.quote}"
          </blockquote>
          <figcaption className="mt-6 text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">
              {vertical.testimonial.author}
            </span>{" "}
            · {vertical.testimonial.role}
          </figcaption>
        </figure>
      </div>
    </section>
  );
}

/* ---------------- FAQ ---------------- */
function Faq() {
  return (
    <section className="border-t border-white/5 bg-white/[0.02] py-16 md:py-20">
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <h2
          className="text-3xl font-bold md:text-4xl"
          style={{ fontFamily: "var(--font-display)" }}
        >
          Perguntas <span className="gradient-text">frequentes</span>
        </h2>
        <div className="mt-8 space-y-4">
          {CLINICAS_FAQ.map((f) => (
            <details
              key={f.q}
              className="glass-card group rounded-xl p-5 open:bg-white/[0.03]"
            >
              <summary className="cursor-pointer list-none font-semibold">
                {f.q}
              </summary>
              <p className="mt-3 text-sm text-muted-foreground">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- FINAL CTA ---------------- */
function FinalCta({ vertical }: { vertical: ClinicaVertical }) {
  return (
    <section className="py-16 md:py-24">
      <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
        <h2
          className="text-3xl font-bold md:text-4xl"
          style={{ fontFamily: "var(--font-display)" }}
        >
          Pronto para transformar a operação da sua{" "}
          <span className="gradient-text">{vertical.shortName.toLowerCase()}</span>?
        </h2>
        <p className="mt-4 text-muted-foreground">
          Agende uma demonstração personalizada e veja a Kentro rodando com o cenário da sua clínica.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Button asChild size="lg" className="gradient-primary text-white shadow-glow">
            <Link
              to="/contato"
              search={{ src: "clinicas", vertical: vertical.slug }}
              onClick={() => {
                trackEvent("demo_cta_click", {
                  location: "clinicas_vertical_final_cta",
                  page: `clinicas_${vertical.slug}`,
                  vertical: vertical.slug,
                  target: "form_contato",
                });
                trackMetaPixelCustom("ScheduleDemo", {
                  source: `clinicas_${vertical.slug}`,
                  location: "clinicas_vertical_final_cta",
                  page: `clinicas_${vertical.slug}`,
                  vertical: vertical.slug,
                });
              }}
            >
              Agendar demonstração <ArrowRight className="ml-1 size-4" />
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline">
            <a
              href={KENTRO.whatsappComercialUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                trackEvent("whatsapp_click", {
                  location: "clinicas_vertical_final_cta",
                  page: `clinicas_${vertical.slug}`,
                  vertical: vertical.slug,
                  number: "comercial",
                });
                trackMetaPixel("Contact", {
                  location: "clinicas_vertical_final_cta",
                  page: `clinicas_${vertical.slug}`,
                  vertical: vertical.slug,
                  number: "comercial",
                });
              }}
            >
              <MessageCircle className="mr-1 size-4" /> Falar no WhatsApp
            </a>
          </Button>
          <Button asChild size="lg" variant="ghost">
            <Link to="/clinicas">
              <ArrowLeft className="mr-1 size-4" /> Ver todas as clínicas
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
